// Copyright (c) Ping. All rights reserved.

import type { ActivityKind, ActivityRowKind, ActivitySort } from './types';

export const activityStrings = {
  screenTitle: 'Activity',
  filterAll: 'All',
  filter: (kind: ActivityKind): string => {
    switch (kind) {
      case 'all':
        return 'All';
      case 'dm':
        return 'DMs';
      case 'mention':
        return 'Mentions';
      case 'reminder':
        return 'Reminders';
      case 'missedCall':
        return 'Missed calls';
      case 'reaction':
        return 'Reactions';
      case 'task':
        return 'Tasks';
      case 'system':
        return 'System';
      case 'reply':
        return 'Replies';
      case 'game':
        return 'Games';
      case 'poll':
        return 'Polls';
      case 'meeting':
        return 'Meetings';
    }
  },
  /** Segmented tab labels (Figma All | Unread). */
  tabAll: 'All',
  tabUnread: 'Unread',
  markAllRead: 'Mark all as read',
  close: 'Close',
  // Context-menu actions (Figma `Options` popover).
  menuMarkUnread: 'Mark as unread',
  menuMarkRead: 'Mark as read',
  menuErase: 'Erase',
  menuGoToMessage: 'Go to message',
  menuSelect: 'Select',
  menuMore: 'More',
  menuRemind: 'Remind me about this',
  // Selection-mode footer (Figma `2804:108423`).
  deleteSelected: 'Delete',
  // Remind submenu.
  remind15m: 'in 15 minutes...',
  remind30m: 'in 30 minutes...',
  remind1h: 'in 1 hour...',
  remindTomorrow: 'Tomorrow',
  remindNextWeek: 'Next week',
  remindCustom: 'Custom',
  // Custom reminder sheet.
  customReminderTitle: 'Set custom reminder time',
  customReminderSelect: 'Select',
  customReminderDate: 'Set date',
  customReminderTime: 'Set time',
  customReminderCancel: 'Cancel',
  customReminderCreate: 'Create',
  // Detail-pane placeholder (desktop master-detail).
  detailPlaceholder: 'Select an activity to view details.',
  sortLabel: 'Sort',
  sort: (sort: ActivitySort): string => {
    switch (sort) {
      case 'mostRecent':
        return 'Most recent';
      case 'byPerson':
        return 'By person';
      case 'byChannel':
        return 'By channel';
    }
  },
  emptyTitle: (filter: ActivityKind): string => {
    if (filter === 'all') {
      return 'Nothing here yet';
    }
    return `No ${activityStrings.filter(filter).toLowerCase()} yet`;
  },
  emptyBody:
    "When someone mentions you, reacts to your messages, or replies in a thread — it'll show up here.",
  errorTitle: "Couldn't load activity",
  errorRetry: 'Try again',
  rowKindLabel: (kind: ActivityRowKind): string => {
    switch (kind) {
      case 'dm':
        return 'Direct message';
      case 'mention':
        return 'Mention';
      case 'reminder':
        return 'Reminder';
      case 'missedCall':
        return 'Missed call';
      case 'reaction':
        return 'Reaction';
      case 'task':
        return 'Task';
      case 'system':
        return 'System';
      case 'reply':
        return 'Reply';
      case 'game':
        return 'Game';
      case 'poll':
        return 'Poll';
      case 'meeting':
        return 'Meeting';
    }
  },
} as const;

export type ActivityStrings = typeof activityStrings;
